import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";

const POSTS_COLLECTION = "posts";

function generateSlug(title) {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function estimateReadingTime(htmlContent) {
  const text = htmlContent.replace(/<[^>]*>/g, "");
  const words = text.split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.ceil(words / 200));
  return `${minutes} min read`;
}

export async function getAllPosts({ publishedOnly = false } = {}) {
  const constraints = [orderBy("createdAt", "desc")];
  if (publishedOnly) {
    constraints.push(where("published", "==", true));
  }
  const q = query(collection(db, POSTS_COLLECTION), ...constraints);
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getPostsByPage(page, perPage = 9, { publishedOnly = true } = {}) {
  const allPosts = await getAllPosts({ publishedOnly });
  const start = (page - 1) * perPage;
  const end = start + perPage;
  return {
    posts: allPosts.slice(start, end),
    total: allPosts.length,
    totalPages: Math.ceil(allPosts.length / perPage),
  };
}

export async function getPostBySlug(slug) {
  const q = query(collection(db, POSTS_COLLECTION), where("slug", "==", slug));
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const docSnap = snapshot.docs[0];
  return { id: docSnap.id, ...docSnap.data() };
}

export async function getPostById(id) {
  const docRef = doc(db, POSTS_COLLECTION, id);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  return { id: docSnap.id, ...docSnap.data() };
}

export async function getRelatedPosts(currentSlug, count = 3) {
  const current = await getPostBySlug(currentSlug);
  if (!current) return [];

  const q = query(
    collection(db, POSTS_COLLECTION),
    where("published", "==", true),
    orderBy("createdAt", "desc")
  );
  const snapshot = await getDocs(q);
  const posts = snapshot.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((p) => p.slug !== currentSlug)
    .sort((a, b) => {
      if (a.category === current.category && b.category !== current.category) return -1;
      if (a.category !== current.category && b.category === current.category) return 1;
      return 0;
    });
  return posts.slice(0, count);
}

export async function searchPosts(queryText) {
  const q = query(collection(db, POSTS_COLLECTION), where("published", "==", true));
  const snapshot = await getDocs(q);
  const lowerQuery = queryText.toLowerCase().trim();
  if (!lowerQuery) return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

  return snapshot.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter(
      (p) =>
        p.title.toLowerCase().includes(lowerQuery) ||
        (p.excerpt && p.excerpt.toLowerCase().includes(lowerQuery)) ||
        (p.tags && p.tags.some((t) => t.toLowerCase().includes(lowerQuery)))
    );
}

export async function getPostsByCategory(category) {
  const q = query(
    collection(db, POSTS_COLLECTION),
    where("published", "==", true),
    where("category", "==", category)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getPostsByTag(tag) {
  const q = query(
    collection(db, POSTS_COLLECTION),
    where("published", "==", true),
    where("tags", "array-contains", tag)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getAllTags() {
  const q = query(collection(db, POSTS_COLLECTION), where("published", "==", true));
  const snapshot = await getDocs(q);
  const tagCounts = {};
  snapshot.docs.forEach((d) => {
    const data = d.data();
    if (data.tags) {
      data.tags.forEach((tag) => {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      });
    }
  });
  return Object.entries(tagCounts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}

export async function createPost(postData) {
  const slug = postData.slug || generateSlug(postData.title);
  const readingTime = estimateReadingTime(postData.content || "");
  const now = serverTimestamp();

  const newPost = {
    ...postData,
    slug,
    readingTime,
    createdAt: now,
    updatedAt: now,
    published: postData.published || false,
    tags: postData.tags || [],
  };

  const docRef = await addDoc(collection(db, POSTS_COLLECTION), newPost);
  return { id: docRef.id, ...newPost };
}

export async function updatePost(id, postData) {
  const updateData = { ...postData, updatedAt: serverTimestamp() };
  if (postData.title && !postData.slug) {
    updateData.slug = generateSlug(postData.title);
  }
  if (postData.content) {
    updateData.readingTime = estimateReadingTime(postData.content);
  }
  if (postData.tags === undefined) {
    updateData.tags = [];
  }

  const docRef = doc(db, POSTS_COLLECTION, id);
  await updateDoc(docRef, updateData);
  return { id, ...updateData };
}

export async function deletePost(id) {
  const docRef = doc(db, POSTS_COLLECTION, id);
  await deleteDoc(docRef);
}

export async function getAllPostsWithStatus() {
  const q = query(collection(db, POSTS_COLLECTION), orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}
